import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    Stack,
    Typography,
    IconButton,
    FormControlLabel,
    Switch,
    CircularProgress,
    Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import {
    PriceConfiguratorQuestions,
    PriceConfiguratorAnswers,
} from '../interfaces/Interfaces';

interface PriceDialog {
    open: boolean;
    onClose: () => void;
    questions: PriceConfiguratorQuestions[];
    answers: PriceConfiguratorAnswers[];
    initialQuestion: PriceConfiguratorQuestions;
    customerRoles?: string[];
    onConfiguratorAnswerSelect?: (answer: PriceConfiguratorAnswers) => void;
}

const PriceConfiguratorDialog: React.FC<PriceDialog> = ({
    open,
    onClose,
    questions,
    answers,
    initialQuestion,
    customerRoles,
    onConfiguratorAnswerSelect,
}) => {
    const [currentQuestion, setCurrentQuestion] =
        useState<PriceConfiguratorQuestions>(initialQuestion);
    const [selectedAnswer, setSelectedAnswer] =
        useState<PriceConfiguratorAnswers | null>(null);
    const [questionHistory, setQuestionHistory] = useState<
        PriceConfiguratorQuestions[]
    >([]);
    const [answerHistory, setAnswerHistory] = useState<
        PriceConfiguratorAnswers[]
    >([]);
    const [prevQuesiton, setPrevQuesiton] =
        useState<PriceConfiguratorQuestions | null>(null);
    const [isComplete, setIsComplete] = useState(false);
    const [wasCompleted, setWasCompleted] = useState(false);
    const [resumeMode, setResumeMode] = useState(false);
    const [loading, setLoading] = useState(false);

    const resetDialog = () => {
        setCurrentQuestion(initialQuestion);
        setQuestionHistory([]);
        setIsComplete(false);
        setWasCompleted(false);
        if (!resumeMode) setPrevQuesiton(null);
    };

    useEffect(() => {
        if (open) {
            setLoading(true);

            setTimeout(() => {
                if (resumeMode) {
                    if (wasCompleted) {
                        setIsComplete(true);
                    } else if (prevQuesiton) {
                        setCurrentQuestion(prevQuesiton);
                    } else {
                        resetDialog();
                    }
                } else {
                    resetDialog();
                }

                setLoading(false);
            }, 500);
        }
    }, [open]);

    const isFirstQuestion =
        currentQuestion.Price_Configurator_Question ===
        initialQuestion.Price_Configurator_Question;

    const filteredAnswers = answers.filter((a) => {
        if (isFirstQuestion && customerRoles && customerRoles.length > 0) {
            return (
                a.Price_Configurator_Question ===
                    currentQuestion.Price_Configurator_Question &&
                customerRoles.some(
                    (role) =>
                        role.trim().toLowerCase() ===
                        (a.Answer ?? '').trim().toLowerCase(),
                )
            );
        }
        return (
            a.Price_Configurator_Question ===
            currentQuestion.Price_Configurator_Question
        );
    });

    const handleAnswerSelect = (answer: PriceConfiguratorAnswers) => {
        setAnswerHistory((prev) => [...prev, answer]);
        setSelectedAnswer(answer);
        onConfiguratorAnswerSelect?.(answer);
        if (onConfiguratorAnswerSelect) {
            onConfiguratorAnswerSelect(answer);
        }
        const nextQuestion = questions.find(
            (q) =>
                q.Price_Configurator_Answer ===
                answer.Price_Configurator_Answer,
        );
        if (nextQuestion) {
            setQuestionHistory((prev) => [...prev, currentQuestion]);
            setCurrentQuestion(nextQuestion);
            setPrevQuesiton(nextQuestion);
        } else {
            setIsComplete(true);
            setWasCompleted(true);
        }
    };

    const handleBack = () => {
        if (questionHistory.length > 0) {
            const previousQuestions = [...questionHistory];
            const lastQuestion = previousQuestions.pop();

            if (lastQuestion) {
                setCurrentQuestion(lastQuestion);
                setQuestionHistory(previousQuestions);
                setPrevQuesiton(lastQuestion);
                setIsComplete(false);
                setWasCompleted(false);

                const updatedAnswerHistory = [...answerHistory];
                updatedAnswerHistory.pop();
                setAnswerHistory(updatedAnswerHistory);

                const prevAnswer = updatedAnswerHistory.at(-1) ?? null;
                setSelectedAnswer(prevAnswer);

                if (onConfiguratorAnswerSelect && prevAnswer) {
                    onConfiguratorAnswerSelect(prevAnswer);
                }
            }
        }
    };

    const handleSaveToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
        setResumeMode(event.target.checked);
    };

    const handleClose = () => {
        if (!isComplete) {
            setPrevQuesiton(currentQuestion);
        }
        onClose();
        if (isComplete && selectedAnswer && onConfiguratorAnswerSelect) {
            onConfiguratorAnswerSelect(selectedAnswer);
        }
    };

    const handleRestart = () => {
        resetDialog();
        setSelectedAnswer(null);
    };

    const renderFooter = (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: 2,
            }}
        >
            {questionHistory.length > 0 ? (
                <IconButton onClick={handleBack}>
                    <ArrowBackIcon />
                </IconButton>
            ) : (
                <Box sx={{ width: 40 }} />
            )}

            <FormControlLabel
                control={
                    <Switch
                        checked={resumeMode}
                        onChange={handleSaveToggle}
                        color="primary"
                    />
                }
                sx={{
                    '& .MuiFormControlLabel-label': {
                        fontSize: '.75em',
                    },
                }}
                label="Save Progress?"
                labelPlacement="top"
            />
        </Box>
    );

    return (
        <>
            <Box sx={{ zIndex: 'modal' }}>
                <Dialog
                    open={open}
                    onClose={handleClose}
                    PaperProps={{
                        sx: {
                            minHeight: '70vh',
                            maxHeight: '70vh',
                            maxWidth: '600px',
                            '@media (min-width: 600px)': {
                                width: '550px',
                            },
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        },
                    }}
                >
                    <Tooltip title="Exit" placement="top">
                        <IconButton
                            aria-label="close"
                            onClick={onClose}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: 8,
                                color: (theme) => theme.palette.grey[500],
                            }}
                            size="large"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>

                    {loading ? (
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : !isComplete ? (
                        <>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 'bold',
                                        marginTop: '15px',
                                        textAlign: 'center',
                                    }}
                                >
                                    {currentQuestion.Question}
                                </Typography>

                                <DialogContent>
                                    <Stack spacing={2} alignItems="center">
                                        {filteredAnswers.map((answer, idx) => (
                                            <Button
                                                key={idx}
                                                variant="contained"
                                                sx={{ width: 400 }}
                                                onClick={() =>
                                                    handleAnswerSelect(answer)
                                                }
                                            >
                                                {answer.Answer}
                                            </Button>
                                        ))}
                                    </Stack>
                                </DialogContent>
                            </Box>

                            {renderFooter}
                        </>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    p: 4,
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{ mt: 2, fontWeight: 'bold' }}
                                >
                                    Thank you for Answering our Questions!
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                    The Table now reflects products based on
                                    your answers.
                                </Typography>
                                <DialogContent
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'center',
                                        gap: 2,
                                        py: 2,
                                    }}
                                >
                                    <Button
                                        variant="contained"
                                        sx={{ minWidth: 120 }}
                                        onClick={handleRestart}
                                    >
                                        Restart
                                    </Button>
                                    <Button
                                        variant="contained"
                                        sx={{ minWidth: 120 }}
                                        onClick={handleClose}
                                    >
                                        View Results
                                    </Button>
                                </DialogContent>
                            </Box>

                            {renderFooter}
                        </>
                    )}
                </Dialog>
            </Box>
        </>
    );
};

export default PriceConfiguratorDialog;
